import React, { memo } from 'react';
import { Card, Checkbox, FormControl, FormControlLabel, FormGroup, FormHelperText, InputLabel, MenuItem, Radio, RadioGroup, Rating, Select, SelectChangeEvent, TextField, Typography } from "@mui/material";
import { Question } from "../types";
import { useAppSelector, useAppDispatch } from '../app/hook';
import { selectAnswerById, setValues, setOtherInfo, updateValue } from '../features/answerList/answerListSlice';
import { selectErrorById, setValue, setErrorText } from '../features/errorList/errorListSlice';
import blankimage from '../image/blankimage.png';

const QuestionBox = memo(function QuestionBox(props: Question) {
    const answer = useAppSelector(selectAnswerById(props.id));
    const error = useAppSelector(selectErrorById(props.id));
    const dispatch = useAppDispatch();

    if (!answer || !error) return null;

    const handleInputOther = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setOtherInfo({id:props.id, value: event.target.value}));
    }

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setValues({id:props.id, value: [(event.target as HTMLInputElement).value]}));

        if(props.required) {
            dispatch(setValue({id:props.id, value: false}));
            dispatch(setErrorText({id:props.id, value: ""}));
        }
    };

    const handleRadioClick = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
        const clickValue = (event.target as any).defaultValue;
        if(clickValue === answer.values[0]) {
            dispatch(setValues({id:props.id, value: [""]}));

            if(props.required) {
                dispatch(setValue({id:props.id, value: true}));
                dispatch(setErrorText({id:props.id, value: "The field is required"}));
            }
        }
    }

    const handleSelectChange = (event: SelectChangeEvent) => {
        dispatch(setValues({id:props.id, value: [event.target.value]}));

        if(props.required) {
            if(event.target.value === "") {
                dispatch(setValue({id:props.id, value: true}));
                dispatch(setErrorText({id:props.id, value: "The field is required"}));
            }
            else {
                dispatch(setValue({id:props.id, value: false}));
                dispatch(setErrorText({id:props.id, value: ""}));
            }
        }
    }

    const handleCheckChange = (index: number) => {
        dispatch(updateValue({id:props.id, index: index, value: !answer.values[index]}));

        dispatch(setValue({id:props.id, value: false}));
        dispatch(setErrorText({id:props.id, value: ""}));
    }

    const handleTextChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setValues({id:props.id, value: [event.target.value]}));

        if(props.required) {
            if(event.target.value === "") {
                dispatch(setValue({id:props.id, value: true}));
                dispatch(setErrorText({id:props.id, value: "The field is required"}));
            }
            else {
                dispatch(setValue({id:props.id, value: false}));
                dispatch(setErrorText({id:props.id, value: ""}));
            }
        }
    }

    const handleNumberChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setValues({id:props.id, value: [event.target.value]}));

        if(props.required) {
            if(event.target.value === "") {
                dispatch(setValue({id:props.id, value: true}));
                dispatch(setErrorText({id:props.id, value: "The field is required"}));
            }
            else {
                dispatch(setValue({id:props.id, value: false}));
                dispatch(setErrorText({id:props.id, value: ""}));
            }
        }
    }

    const handleRatingChange = (event: React.SyntheticEvent<Element, Event>, newValue: number | null) => {
        dispatch(setValues({id:props.id, value: [newValue]}));

        if(props.required) {
            if(newValue === null) {
                dispatch(setValue({id:props.id, value: true}));
                dispatch(setErrorText({id:props.id, value: "The field is required"}));
            }
            else {
                dispatch(setValue({id:props.id, value: false}));
                dispatch(setErrorText({id:props.id, value: ""}));
            }
        }
    }

    const handleScoreChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        dispatch(setValues({id:props.id, value: [parseInt((event.target as HTMLInputElement).value)]}));

        if(props.required) {
            dispatch(setValue({id:props.id, value: false}));
            dispatch(setErrorText({id:props.id, value: ""}));
        }
    };

    return (
        <Card className={"flex flex-col mx-auto mt-5 p-5 w-4/5 lg:w-2/4"}>
            <Typography sx={{ fontSize: 20 }} component="div">
                {props.required && <span className="text-red-600">* </span>}
                <div className="richtext-html" dangerouslySetInnerHTML={{ __html: props.content }} />
            </Typography>
            {
                props.image !== undefined && props.image !== null
                &&
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                    <img src={props.image} style={{ width: 200, height: 200 }} alt="" loading="lazy" className='mt-10 mb-10 self-center' />
                </div>
            }
            {
                props.variant === "single"
                &&
                <FormControl 
                    sx={{ m: 1}}
                    error={error.value}
                >
                    <RadioGroup
                        value={answer.values[0] || ''}
                        onChange={handleRadioChange}
                    >
                        {
                            props.options?.map((item, index) => {
                                return (
                                    <FormControlLabel key={index} value={item} control={<Radio onClick={(e) => {handleRadioClick(e)}} />} label={item} />
                                )
                            })
                        }
                        {
                            props.otherOption
                            &&
                            <FormControlLabel
                                value="other"
                                control={<Radio onClick={handleRadioClick} />}
                                label={
                                        answer.values[0] === "other" ? (
                                        <TextField
                                            disabled={answer.values[0] !== "other"}
                                            label="Please Specify"
                                            variant="standard"
                                            onChange={handleInputOther}
                                            value={answer.otherInfo || ''}
                                        />
                                    ) : (
                                        "Other"
                                    )
                                }
                            />
                        }
                    </RadioGroup>
                    <FormHelperText>{error.errorText}</FormHelperText>
                </FormControl>
            }
            {
                props.variant === "singlewm"
                &&
                <FormControl 
                    sx={{ m: 1}}
                    error={error.value}
                >
                    <RadioGroup
                        value={answer.values[0] || ''}
                        onChange={handleRadioChange}
                    >
                        {
                            props.options?.map((item, index) => {
                                const isEvenIndex = index % 2 === 0;

                                if (isEvenIndex) {
                                    return (
                                        <div key={index} style={{ display: 'flex' }}>
                                            <div style={{ flex: '1', marginRight: '10px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    <span>
                                                        <FormControlLabel
                                                            value={item}
                                                            control={<Radio onClick={(e) => { handleRadioClick(e) }} />}
                                                            label={item}
                                                        />
                                                    </span>
                                                    <span>
                                                        {(props.images !== undefined && props.images[index])
                                                            ?
                                                            <img
                                                                src={props.images[index]}
                                                                style={{ width: 200, height: 200 }}
                                                                alt=""
                                                                loading="lazy"
                                                                className="mt-10 mb-10 self-center"
                                                            />
                                                            :
                                                            <img
                                                                src={blankimage}
                                                                style={{ width: 200, height: 200 }}
                                                                alt=""
                                                                loading="lazy"
                                                                className="mt-10 mb-10 self-center"
                                                            />
                                                            }
                                                    </span>
                                                </div>
                                            </div>
                                            {(index + 1 !== props.options?.length ?
                                                 <div key={index + 1} style={{ flex: '1', marginLeft: '10px' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                        <FormControlLabel
                                                            value={props.options !== undefined && props.options[index + 1]}
                                                            control={<Radio onClick={(e) => { handleRadioClick(e) }} />}
                                                            label={props.options !== undefined && props.options[index + 1]}
                                                        />
                                                        {(props.images !== undefined && props.images[index + 1])
                                                            ?
                                                            <img
                                                                src={props.images[index + 1]}
                                                                style={{ width: 200, height: 200 }}
                                                                alt=""
                                                                loading="lazy"
                                                                className="mt-10 mb-10 self-center"
                                                            />
                                                            :
                                                            <img
                                                                src={blankimage}
                                                                style={{ width: 200, height: 200 }}
                                                                alt=""
                                                                loading="lazy"
                                                                className="mt-10 mb-10 self-center"
                                                            />
                                                            }
                                                    </div>
                                                 </div>
                                                :
                                                <div></div>
                                            )}
                                        </div>
                                    )
                                }
                                return null;
                            })
                        }
                        {
                            props.otherOption
                            &&
                            <FormControlLabel
                                value="other"
                                control={<Radio onClick={handleRadioClick} />}
                                label={
                                        answer.values[0] === "other" ? (
                                        <TextField
                                            disabled={answer.values[0] !== "other"}
                                            label="Please Specify"
                                            variant="standard"
                                            onChange={handleInputOther}
                                            value={answer.otherInfo || ''}
                                        />
                                    ) : (
                                        "Other"
                                    )
                                }
                            />
                        }
                    </RadioGroup>
                    <FormHelperText>{error.errorText}</FormHelperText>
                </FormControl>
            }
            {
                props.variant === "multiple"
                &&
                <FormControl 
                    component="fieldset" 
                    variant="standard" 
                    error={error.value}
                >
                    <FormGroup sx={{ m: 1}}>
                        {
                            props.options?.map((item,index) => {
                                return (
                                    <FormControlLabel 
                                        key={index} 
                                        control={
                                            <Checkbox 
                                                checked={answer.values[index]} 
                                                onChange={(e) => handleCheckChange(index)} 
                                            />} 
                                        label={item} />
                                )
                            })
                        }
                        {
                            props.otherOption
                            &&
                            <FormControlLabel
                                value="other"
                                control={
                                    <Checkbox
                                        checked={answer.values[answer.values.length - 1]} 
                                        onChange={(e) => handleCheckChange(answer.values.length - 1)} 
                                    />
                                }
                                label={
                                        answer.values[answer.values.length - 1] === true ? (
                                        <TextField
                                            disabled={answer.values[answer.values.length - 1] !== true}
                                            label="Please Specify"
                                            variant="standard"
                                            onChange={handleInputOther}
                                            value={answer.otherInfo || ''}
                                        />
                                    ) : (
                                        "Other"
                                    )
                                }
                            />
                        }
                    </FormGroup>
                    <FormHelperText>{error.errorText}</FormHelperText>
                </FormControl>
            }
            {
                props.variant === "multiplewm"
                &&
                <FormControl 
                    component="fieldset" 
                    variant="standard" 
                    error={error.value}
                >
                    <FormGroup sx={{ m: 1}}>
                    {
                            props.options?.map((item, index) => {
                                const isEvenIndex = index % 2 === 0;

                                if (isEvenIndex) {
                                    return (
                                        <div key={index} style={{ display: 'flex' }}>
                                            <div style={{ flex: '1', marginRight: '10px' }}>
                                                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                    <span>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={answer.values[index]}
                                                                    onChange={(e) => handleCheckChange(index)}
                                                                />
                                                            }
                                                            label={item}
                                                        />
                                                    </span>
                                                    <span>
                                                        {(props.images !== undefined && props.images[index])
                                                            ?
                                                            <img
                                                                src={props.images[index]}
                                                                style={{ width: 200, height: 200 }}
                                                                alt=""
                                                                loading="lazy"
                                                                className="mt-10 mb-10 self-center"
                                                            />
                                                            :
                                                            <img
                                                                src={blankimage}
                                                                style={{ width: 200, height: 200 }}
                                                                alt=""
                                                                loading="lazy"
                                                                className="mt-10 mb-10 self-center"
                                                            />}
                                                    </span>
                                                </div>
                                            </div>
                                            {(index + 1 !== props.options?.length ?
                                                <div key={index + 1} style={{ flex: '1', marginLeft: '10px' }}>
                                                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                                                        <FormControlLabel
                                                            control={
                                                                <Checkbox
                                                                    checked={answer.values[index + 1]}
                                                                    onChange={(e) => handleCheckChange(index + 1)}
                                                                />
                                                            }
                                                            label={props.options !== undefined && props.options[index + 1]}
                                                        />
                                                        {(props.images !== undefined && props.images[index + 1])
                                                            ?
                                                            <img
                                                                src={props.images[index + 1]}
                                                                style={{ width: 200, height: 200 }}
                                                                alt=""
                                                                loading="lazy"
                                                                className="mt-10 mb-10 self-center"
                                                            />
                                                            :
                                                            <img
                                                                src={blankimage}
                                                                style={{ width: 200, height: 200 }}
                                                                alt=""
                                                                loading="lazy"
                                                                className="mt-10 mb-10 self-center"
                                                            />}
                                                    </div>
                                                </div>
                                                :
                                                <div></div>
                                            )}
                                        </div>
                                    )
                                }
                                return null;
                            })
                        }
                        {
                            props.otherOption
                            &&
                            <FormControlLabel
                                value="other"
                                control={
                                    <Checkbox
                                        checked={answer.values[answer.values.length - 1]} 
                                        onChange={(e) => handleCheckChange(answer.values.length - 1)} 
                                    />
                                }
                                label={
                                        answer.values[answer.values.length - 1] === true ? (
                                        <TextField
                                            disabled={answer.values[answer.values.length - 1] !== true}
                                            label="Please Specify"
                                            variant="standard"
                                            onChange={handleInputOther}
                                            value={answer.otherInfo || ''}
                                        />
                                    ) : (
                                        "Other"
                                    )
                                }
                            />
                        }
                    </FormGroup>
                    <FormHelperText>{error.errorText}</FormHelperText>
                </FormControl>
            }
            {
                props.variant === "dropdown"
                &&
                <FormControl 
                    fullWidth 
                    sx={{ m: 1, minWidth: 80, width: 250 }}
                    error={error.value}
                >
                    <InputLabel id="select-helper-label">Select</InputLabel>
                    <Select
                        labelId="select-helper-label"
                        label="Select"
                        onChange={handleSelectChange}
                        value={answer.values[0] || ''}
                    >
                        <MenuItem value=''>
                            <Typography color="text.secondary" gutterBottom>
                                Select
                            </Typography>
                        </MenuItem>
                        {
                            props.options?.map((item,index) => {
                                return (
                                    <MenuItem key={index} value={item}>{item}</MenuItem>
                                )
                            })
                        }
                    </Select>
                    <FormHelperText>{error.errorText}</FormHelperText>
                </FormControl>
            }
            {
                props.variant === "text"
                &&
                <TextField 
                    className='w-3/4 lg:w-2/4'
                    label="answer" 
                    variant="standard"
                    value={answer.values[0] || ''}
                    onChange={handleTextChange}
                    error={error.value}
                    helperText={error.errorText}
                />
            }
            {
                props.variant === "number"
                &&
                <TextField 
                    sx={{ m: 1, width: 200 }}
                    label="number" 
                    type="number"
                    InputLabelProps={{
                        shrink: true,
                    }} 
                    value={answer.values[0] || ''}
                    onChange={handleNumberChange}
                    error={error.value}
                    helperText={error.errorText}
                />
            }
            {
                props.variant === "rating"
                &&
                <FormControl 
                    error={error.value}
                >
                    <Rating
                        value={answer.values[0] || null}
                        onChange={handleRatingChange}
                    />
                    <FormHelperText>{error.errorText}</FormHelperText>
                </FormControl>
            }
            {
                props.variant === "score"
                &&
                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                    <span>
                        {props.scorewd && props.scorewd[0] !== undefined ? props.scorewd[0] : null}
                    </span>
                    <FormControl>
                        <RadioGroup
                            row
                            aria-labelledby="demo-row-radio-buttons-group-label"
                            name="row-radio-buttons-group"
                            value={answer.values[0] || null}
                            onChange={handleScoreChange}
                        >
                            <FormControlLabel value={1} control={<Radio />} label="1" labelPlacement="top" />
                            <FormControlLabel value={2} control={<Radio />} label="2" labelPlacement="top" />
                            <FormControlLabel value={3} control={<Radio />} label="3" labelPlacement="top" />
                            <FormControlLabel value={4} control={<Radio />} label="4" labelPlacement="top" />
                            <FormControlLabel value={5} control={<Radio />} label="5" labelPlacement="top" />
                        </RadioGroup>
                    </FormControl>
                    <span>
                        {props.scorewd && props.scorewd[1] !== undefined ? props.scorewd[1] : null}
                    </span>
                </div>
            }
        </Card>
    )
});

export default QuestionBox;
